import { motion } from "framer-motion";
import { ArrowRight, MapPin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { CASES, GUEST_BOOKING_URLS, BRAND } from "@/lib/constants";

export default function ForGaester() {
  return (
    <div className="bg-white text-[#222222]" data-testid="for-gaester-page">
      <Header />

      <section className="relative bg-[#111111] text-white pt-28 sm:pt-36 pb-14 sm:pb-20 overflow-hidden">
        <div className="absolute -top-32 right-0 w-[36rem] h-[36rem] glow-gold pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.045] select-none">
          <span
            className="font-serif text-[24vw] leading-none italic"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Ophold
          </span>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
            <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
            For gæster
          </p>
          <h1
            className="font-serif text-[2.5rem] sm:text-6xl lg:text-[5.5rem] text-white font-light tracking-tight leading-[1.02] max-w-5xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Book dit næste{" "}
            <span className="italic text-[#D4AF37]">ophold</span>.
          </h1>
          <p className="mt-6 text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            Alle vores boliger til korttidsudlejning samlet ét sted - klar til
            din næste weekend, ferie eller forretningsrejse.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 lg:gap-14">
            {CASES.filter((c) => c.slug in GUEST_BOOKING_URLS).map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="group scroll-mt-28"
                id={c.slug}
                data-testid={`guest-property-${c.slug}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#F8F8F8]">
                  <img
                    src={c.heroImage}
                    alt={c.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/50 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 bg-[#D4AF37] text-[#111111] px-3 py-1.5">
                    <div className="text-[9px] tracking-[0.28em] uppercase font-medium">
                      Ledig · Book nu
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <div className="flex items-center gap-2 text-xs text-[#555555] mb-3">
                    <MapPin size={13} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <span>{c.location}</span>
                  </div>
                  <h2
                    className="font-serif text-2xl sm:text-3xl lg:text-[2rem] text-[#111111] font-light leading-tight"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {c.name}
                  </h2>
                  <p className="mt-3 text-[#555555] font-light leading-relaxed text-sm sm:text-base">
                    {c.summary}
                  </p>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <a
                      href={GUEST_BOOKING_URLS[c.slug] || "#"}
                      target={GUEST_BOOKING_URLS[c.slug] && GUEST_BOOKING_URLS[c.slug] !== "#" ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={`guest-book-${c.slug}`}
                      className="inline-flex items-center justify-center gap-2 bg-[#111111] text-white px-5 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#111111] transition-colors duration-500"
                    >
                      <span>Book ophold</span>
                      <ExternalLink size={13} strokeWidth={1.5} />
                    </a>
                    <Link
                      to={`/firmaboliger/${c.slug}`}
                      className="inline-flex items-center justify-center gap-2 border border-[#111111]/20 text-[#111111] px-5 py-3 text-[11px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#C9A227] transition-colors"
                    >
                      <span>Se bolig</span>
                      <ArrowRight size={13} strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 sm:mt-20 pt-12 border-t border-black/10 text-center max-w-2xl mx-auto">
            <p className="text-[#555555] font-light leading-relaxed">
              Spørgsmål til et ophold eller ønsker du at booke direkte? Ring på{" "}
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="text-[#111111] link-underline"
              >
                {BRAND.phone}
              </a>{" "}
              eller skriv til{" "}
              <a
                href={`mailto:${BRAND.email}`}
                className="text-[#111111] link-underline"
              >
                {BRAND.email}
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
